import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Import mock strategies from StrategyBuilder
import { EXAMPLE_STRATEGIES } from './StrategyBuilder.tsx';

// =====================
// MOCK VAULTS SECTION
// =====================

export const EXAMPLE_VAULTS = [
    {
        name: 'STRK Growth Vault',
        on: true,
        strategies: [
            { name: 'Leveraged STRK Staking', pct: 60 },
            { name: 'ETH/USDC LP DCA', pct: 40 },
        ],
    },
    {
        name: 'Yield Diversified',
        on: false,
        strategies: [
            { name: 'Perps Yield Farming', pct: 50 },
            { name: 'ETH/USDC LP DCA', pct: 50 },
        ],
    },
    {
        name: 'All Weather Vault',
        on: false,
        strategies: [
            { name: 'Leveraged STRK Staking', pct: 34 },
            { name: 'ETH/USDC LP DCA', pct: 33 },
            { name: 'Perps Yield Farming', pct: 33 },
        ],
    },
];

// Vault type: { name: string, strategies: [{ name: string, pct: number }], on: boolean }

const VAULT_COLORS = ['#06b6d4', '#818cf8', '#f472b6', '#facc15', '#34d399'];

const VaultBuilder: React.FC = () => {
    // Mock: get available strategies from the mock data
    const availableStrategies = EXAMPLE_STRATEGIES;

    // Vault creation state
    const [vaultName, setVaultName] = useState('');
    const [vaultStrategies, setVaultStrategies] = useState<{ name: string; pct: number }[]>([]);
    const [savedVaults, setSavedVaults] = useState<any[]>(EXAMPLE_VAULTS);
    const [selectedVaultIdx, setSelectedVaultIdx] = useState<number | null>(null);

    // Add a strategy to the vault
    const handleAddStrategy = () => {
        setVaultStrategies([...vaultStrategies, { name: '', pct: 0 }]);
    };
    // Update a strategy selection or pct
    const handleStrategyChange = (idx: number, field: 'name' | 'pct', value: string | number) => {
        setVaultStrategies(vaultStrategies =>
            vaultStrategies.map((s, i) =>
                i === idx ? { ...s, [field]: field === 'pct' ? Number(value) : value } : s
            )
        );
    };
    // Remove a strategy from the vault
    const handleRemoveStrategy = (idx: number) => {
        setVaultStrategies(vaultStrategies => vaultStrategies.filter((_, i) => i !== idx));
    };
    // Save the vault
    const handleSaveVault = () => {
        if (!vaultName.trim() || vaultStrategies.length === 0) return;
        setSavedVaults(vaults => [
            ...vaults,
            { name: vaultName, strategies: vaultStrategies, on: false },
        ]);
        setVaultName('');
        setVaultStrategies([]);
    };
    // Toggle vault on/off
    const handleToggleVault = (idx: number) => {
        setSavedVaults(vaults => vaults.map((v, i) => i === idx ? { ...v, on: !v.on } : v));
    };

    // Total allocation
    const totalPct = vaultStrategies.reduce((sum, s) => sum + (s.pct || 0), 0);

    // Pie chart data
    const selectedVault = selectedVaultIdx !== null ? savedVaults[selectedVaultIdx] : null;
    const pieData = selectedVault ? selectedVault.strategies.map((s: any) => ({ name: s.name, value: s.pct })) : [];

    return (
        <div className="flex flex-col md:flex-row gap-8 p-6 text-white min-h-[80vh]">
            <div className="md:w-1/2 w-full bg-gray-950 rounded-lg p-6 flex flex-col">
                <h2 className="text-xl font-semibold mb-4">Vault Creation Tool</h2>
                {/* Vault creation form */}
                <div className="bg-gray-800 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                        <input
                            className="bg-gray-900 text-white rounded p-2 flex-1"
                            placeholder="Vault name..."
                            value={vaultName}
                            onChange={e => setVaultName(e.target.value)}
                        />
                        <button
                            className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded font-semibold"
                            onClick={handleSaveVault}
                            disabled={!vaultName.trim() || vaultStrategies.length === 0 || totalPct !== 100}
                        >
                            Save Vault
                        </button>
                    </div>
                    <div className="mb-2 text-sm text-gray-400">Total allocation: <span className={totalPct === 100 ? 'text-green-400' : 'text-red-400'}>{totalPct}%</span> (must be 100%)</div>
                    {vaultStrategies.map((s, idx) => (
                        <div key={idx} className="flex items-center gap-2 mb-2">
                            <select
                                className="bg-gray-900 text-white rounded p-2 flex-1"
                                value={s.name}
                                onChange={e => handleStrategyChange(idx, 'name', e.target.value)}
                            >
                                <option value="">Select strategy...</option>
                                {availableStrategies.map(strat => (
                                    <option key={strat.name} value={strat.name}>
                                        {strat.name} — APY: {strat.apy}% | Risk: {strat.risk} | Used: {strat.usedCount}x
                                    </option>
                                ))}
                            </select>
                            <input
                                type="number"
                                className="bg-gray-900 text-white rounded p-2 w-24"
                                value={s.pct}
                                min={0}
                                max={100}
                                onChange={e => handleStrategyChange(idx, 'pct', e.target.value)}
                                placeholder="%"
                            />
                            <span className="text-gray-400">%</span>
                            <button
                                className="text-red-400 hover:text-red-600 text-sm px-2"
                                onClick={() => handleRemoveStrategy(idx)}
                                title="Remove"
                            >✕</button>
                        </div>
                    ))}
                    <button
                        className="bg-cyan-800 hover:bg-cyan-900 text-white px-4 py-2 rounded mt-2"
                        onClick={handleAddStrategy}
                        disabled={vaultStrategies.length >= availableStrategies.length}
                    >
                        + Add Strategy
                    </button>
                </div>
                {/* Saved vaults list */}
                <h2 className="text-lg font-semibold mb-2">Saved Vaults</h2>
                <div className="space-y-2">
                    {savedVaults.map((v, idx) => (
                        <div
                            key={v.name}
                            className={`flex items-center gap-2 bg-gray-800 rounded p-2 cursor-pointer transition border-2 ${selectedVaultIdx === idx ? 'border-cyan-400' : 'border-transparent'}`}
                            onClick={() => setSelectedVaultIdx(idx)}
                        >
                            <span className="flex-1 truncate font-semibold text-cyan-300">{v.name}</span>
                            <span className="text-xs text-gray-400">{v.strategies.map(s => `${s.name} (${s.pct}%)`).join(', ')}</span>
                        </div>
                    ))}
                </div>
            </div>
            {/* Right: Vault Pie Chart */}
            <div className="md:w-1/2 w-full bg-gray-950 rounded-lg p-6 flex flex-col items-center justify-center">
                {selectedVault ? (
                    <>
                        <h2 className="text-xl font-semibold mb-4">Investment Split</h2>
                        <ResponsiveContainer width={500} height={500}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    animationDuration={400}
                                >
                                    {pieData.map((entry, idx) => (
                                        <Cell key={`cell-${idx}`} fill={VAULT_COLORS[idx % VAULT_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value: number) => `${value}%`} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </>
                ) : (
                    <div className="bg-gray-800 rounded-lg p-8 text-gray-400 text-center">
                        Select a vault to see its investment split.
                    </div>
                )}
            </div>
        </div>
    );
};

export default VaultBuilder; 